import { ReactNode, useCallback, useMemo } from 'react';
import { Checkbox } from '@karnott/form';
import { Column, CustomColumnDef, NoDataColumn, RowId, RowShape } from '../types';

function isNoDataColumn<Row extends RowShape>(c: Column<Row>): c is NoDataColumn<Row> {
  return !c.key && !!(c as NoDataColumn<Row>).displayFn;
}

type UseTableProps<Row extends RowShape> = {
  rows: Row[];
  columns: Column<Row>[];
  selection?: RowId[];
  onSelectionChange: (selectedIds: RowId[]) => void;
};

export function useTable<Row extends RowShape>({ columns, rows, selection, onSelectionChange }: UseTableProps<Row>) {
  const toggleAllRows = useCallback(() => {
    onSelectionChange(selection?.length === rows.length ? [] : rows.map((r) => r.id));
  }, [onSelectionChange, rows, selection?.length]);

  /* Column definitions */
  const columnDefs = useMemo(() => {
    return columns
      .filter(Boolean)
      .map((c, i) => {
        const columnDef: CustomColumnDef<Row> = {
          type: 'key',
          id: `no_data_${i}`,
          displayTotal: c.total !== false || false,
          displayOnHover: c.displayOnHover || false,
          width: c.width,
          renderHeader: () => c.header ?? null,
          renderCell: () => null,
          originalColumn: c,
        };
        if (c.key) {
          // key column
          columnDef.id = c.key;
          columnDef.renderCell = (row) => {
            const keySplits = c.key.split('.');
            // oxlint-disable-next-line typescript/no-explicit-any
            let value: any = row;
            for (const split of keySplits) {
              value = value[split];
            }
            return value === null || value === undefined
              ? '-'
              : (c.displayFn?.({
                  ...row,
                  row: row,
                }) ?? (value as ReactNode));
          };
        } else if (isNoDataColumn(c)) {
          // data column
          columnDef.type = 'data';
          columnDef.renderCell = (row) => {
            return (
              <div className="cell_no_data" style={{ justifyContent: c.justifyContent }}>
                {c.displayFn?.(row)}
              </div>
            );
          };
        } else if ('type' in c && c.type === 'selection') {
          // selection column
          columnDef.type = 'selection';
          columnDef.renderHeader = () =>
            c.header === false ? null : (
              <div className="cell_no_data">
                <Checkbox
                  checked={selection?.length === rows.length}
                  onChange={toggleAllRows}
                  hideLabel
                  label={`Select all / ${i}`}
                />
              </div>
            );

          columnDef.renderCell = (row) => {
            const isSelected = selection?.includes(row.id) ?? false;
            return c.hideSelection?.(row) ? null : (
              <div className="cell_no_data">
                <Checkbox
                  checked={isSelected}
                  onChange={() =>
                    onSelectionChange(
                      (isSelected ? selection?.filter((id) => id !== row.id) : [...(selection || []), row.id]) || [],
                    )
                  }
                  label={`Select ${row.id} / ${i}`}
                  hideLabel
                />
              </div>
            );
          };
        } else {
          return null;
        }

        return columnDef;
      })
      .filter(Boolean);
  }, [columns, onSelectionChange, rows.length, selection, toggleAllRows]);

  return useMemo(() => ({ columnDefs }), [columnDefs]);
}

type UseTotalRowProps<Row extends RowShape> = {
  rows: Row[];
  columnDefs: CustomColumnDef<Row>[];
  displayTotal: boolean;
};
export function useTotalRow<Row extends RowShape>({
  rows,
  columnDefs,
  displayTotal,
}: UseTotalRowProps<Row>): (number | null)[] {
  return useMemo(() => {
    if (!displayTotal) return [];

    // the first column is always reserved for the "total" label
    const cells = Array<number | null>(columnDefs.length - 1);

    // column-level total disabling
    for (let i = 1; i < columnDefs.length; i++) {
      const columnDef = columnDefs[i]!;

      if (!(columnDef.displayTotal || false) && columnDef.type !== 'key') {
        cells[i - 1] = null;
      }
    }

    for (const row of rows) {
      for (let i = 0; i < cells.length; i++) {
        if (cells[i] === null) {
          continue;
        }
        // check above prevents columnDef from not being a KeyColumn
        const columnDef = columnDefs[i + 1]!;
        const value = row[columnDef.id];

        if (typeof value === 'number' || value === undefined || value === null) {
          cells[i] = (cells[i] || 0) + (value || 0);
        } else {
          cells[i] = null;
        }
      }
    }

    return cells;
  }, [columnDefs, displayTotal, rows]);
}
